/**
 * SkyDome — stylized hemispherical gradient sky.
 *
 * Phase 8 Library Edition polish. Replaces the flat warm-ochre `<color attach="background">`
 * in Renderer.tsx with a gradient dome: warm-ochre at the horizon softening to a cooler,
 * muted slate-blue at the zenith. Preserves the Aesthetic Brief Rule 5 fog color at the
 * horizon so distant geometry still reads as matted into the sky.
 *
 * AESTHETIC-BRIEF constraints respected:
 *   - Rule 1: no PBR. MeshBasicMaterial + vertex colors + unlit.
 *   - Rule 4: painted-gradient sky, not HDRI.
 *   - Rule 5: horizon tint MUST match FOG_COLOR so distant geometry blends cleanly.
 *
 * Implementation notes:
 *   - Single SphereGeometry with vertex colors. Y-up sphere: the vertices at y=+radius
 *     (zenith) are tinted slate-blue; vertices at y<=0 (horizon and below) are tinted
 *     warm-ochre. A smooth cosine easing between them avoids a harsh band.
 *   - Rendered with THREE.BackSide so the inside of the sphere faces the camera.
 *   - Large radius (far below Canvas camera.far=1000) so the dome always contains the
 *     scene. `depthWrite={false}` + `renderOrder={-1}` so the dome is always behind
 *     everything (including fog-affected geometry in the foreground).
 *   - Parented to the camera so the dome follows the camera — the sky never "runs out"
 *     even if the player walks far from origin. Zero relative motion = fixed sky.
 *   - `fog={false}` on the material so the dome color itself is not tinted by the fog
 *     at distance; fog tints the FOREGROUND geometry to match the horizon color, which
 *     is the correct direction for this effect.
 *
 * No tests in the first ship — this is pure visual polish with deterministic output.
 * A Phase 9 test could render this into @react-three/test-renderer and assert the
 * material color attribute is present, but that's over-scope for a single visual tweak.
 */
import { useMemo, useRef, useEffect } from 'react';
import { useThree, useFrame } from '@react-three/fiber';
import * as THREE from 'three';
import { FOG_COLOR } from '../lib/const';

// Sky palette — horizon matches FOG_COLOR (Rule 5) so distant geometry blends into the
// sky band cleanly. Zenith is muted slate-blue pulled from the master palette's
// `skyMuted` / cooler-end band to keep the dome within the 12-color envelope (Rule 3).
const HORIZON_COLOR = FOG_COLOR; // #E8D5B7 warm-ochre
const ZENITH_COLOR = '#9DB3C2'; // cooler slate-blue, complementary to horizon ochre
const DOME_RADIUS = 500; // well below camera.far=1000; well above site scale

/**
 * Build the vertex color buffer for a sphere geometry. Returns a Float32Array sized
 * for vertex-color attribute (3 components per vertex, RGB in 0..1). Top of sphere
 * (y=+R) -> ZENITH_COLOR; horizon (y=0) -> HORIZON_COLOR; bottom (y=-R) -> HORIZON_COLOR
 * (the bottom half of the dome is below ground and invisible anyway, but coloring it
 * the horizon color avoids any visible seam if the camera dips or the ground has gaps).
 *
 * Easing: smoothstep on y/R remapped to [0, 1] so 0 = horizon and 1 = zenith. The
 * smoothstep gives a gentler transition than a linear blend without requiring a shader.
 */
function buildGradientColors(positions: THREE.BufferAttribute): Float32Array {
  const horizon = new THREE.Color(HORIZON_COLOR);
  const zenith = new THREE.Color(ZENITH_COLOR);
  const count = positions.count;
  const colors = new Float32Array(count * 3);
  const tmp = new THREE.Color();
  for (let i = 0; i < count; i++) {
    const y = positions.getY(i);
    // Normalize y from [-R, +R] to [0, 1]. Only the top half (y > 0) shows the
    // gradient; bottom half clamps to horizon color to avoid any dark band below.
    const yn = Math.max(0, y / DOME_RADIUS); // [0, 1] for top half
    // smoothstep for nicer falloff: 3t^2 - 2t^3
    const t = yn * yn * (3 - 2 * yn);
    tmp.copy(horizon).lerp(zenith, t);
    colors[i * 3 + 0] = tmp.r;
    colors[i * 3 + 1] = tmp.g;
    colors[i * 3 + 2] = tmp.b;
  }
  return colors;
}

/**
 * SkyDome component. Mount as a child of the Canvas (typically inside SceneGraph
 * alongside BakedLighting). Emits a single backside-rendered sphere mesh parented to
 * the active camera. Returns a JSX mesh; does NOT emit `null`.
 */
export function SkyDome() {
  const { camera } = useThree();
  const meshRef = useRef<THREE.Mesh>(null);

  // Build the geometry once. Sphere subdivisions: 32 width x 16 height = 512 tris — cheap.
  // Reuse across re-renders via useMemo.
  const geometry = useMemo(() => {
    const geo = new THREE.SphereGeometry(DOME_RADIUS, 32, 16);
    const colors = buildGradientColors(
      geo.getAttribute('position') as THREE.BufferAttribute,
    );
    geo.setAttribute('color', new THREE.BufferAttribute(colors, 3));
    return geo;
  }, []);

  // Follow the camera exactly — zero relative motion so the sky never "runs out".
  // useFrame is lighter than re-parenting via scene graph hierarchy (avoids churning
  // the scene parent in and out as cameras change).
  useFrame(() => {
    if (meshRef.current) {
      meshRef.current.position.copy(camera.position);
    }
  });

  // Dispose geometry on unmount — normal Three.js hygiene since we construct it
  // imperatively inside useMemo instead of via JSX.
  useEffect(() => {
    return () => {
      geometry.dispose();
    };
  }, [geometry]);

  return (
    <mesh
      ref={meshRef}
      geometry={geometry}
      renderOrder={-1}
      frustumCulled={false}
    >
      <meshBasicMaterial
        vertexColors
        side={THREE.BackSide}
        depthWrite={false}
        toneMapped={false}
        fog={false}
      />
    </mesh>
  );
}
